import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, ArrowRight, Target, FileText, MessagesSquare, Award, Radar, Search, TerminalSquare, KeyRound, Cloud, Server, Headset, AppWindow, Database, LayoutGrid, CloudCog, BrainCircuit, Container, Workflow, Lightbulb, TrendingUp, CheckCircle2 } from "lucide-react";

const LOGOS = {
  grc: "https://static.prod-images.emergentagent.com/jobs/a4d9da39-dc3c-43e0-8ce6-4ea87ce4f9ce/images/29ce77972c0744c4d84228b407a30736d4a4ced2b731df5195cc41a0badab19d.jpeg",
  soc: "https://static.prod-images.emergentagent.com/jobs/a4d9da39-dc3c-43e0-8ce6-4ea87ce4f9ce/images/46889e1c9fa74ffbda11771da4fef13ed83937ac388dc063e68e0880e04d2343.jpeg",
  iam: "https://static.prod-images.emergentagent.com/jobs/a4d9da39-dc3c-43e0-8ce6-4ea87ce4f9ce/images/c90d6b97fa6557c782728564e483f8043850163114f8b9d0e86a70f5ae6b8ca3.jpeg",
  cloud: "https://static.prod-images.emergentagent.com/jobs/a4d9da39-dc3c-43e0-8ce6-4ea87ce4f9ce/images/94c6d88eb67cbe4329adfb820df5e9e8a3b7112314177dcfc584c671a8b2de02.jpeg",
  sysadmin: "https://static.prod-images.emergentagent.com/jobs/a4d9da39-dc3c-43e0-8ce6-4ea87ce4f9ce/images/7d1384d591aa020e0f19cfb588b221aacef7ea8cea444dc2eb2a69d33a46d7f3.jpeg",
  itsupport: "https://static.prod-images.emergentagent.com/jobs/a4d9da39-dc3c-43e0-8ce6-4ea87ce4f9ce/images/796fbbc76fb18f5e1ba0db7df2fa3490a7b510cf5832a6b655c5d947eeebd474.jpeg",
  cloudapp: "https://static.prod-images.emergentagent.com/jobs/a4d9da39-dc3c-43e0-8ce6-4ea87ce4f9ce/images/3571f225564662ba790071d146b7e21cdfcce275b888fe0a15e8d5d0e27d4eaa.jpeg",
  dba: "https://static.prod-images.emergentagent.com/jobs/a4d9da39-dc3c-43e0-8ce6-4ea87ce4f9ce/images/df6bda47fc9e2dae1e7e3ded1373b2285f170e4f4ea5084baec240005fed083b.jpeg",
  m365: "https://static.prod-images.emergentagent.com/jobs/a4d9da39-dc3c-43e0-8ce6-4ea87ce4f9ce/images/ab27b78b5653cf5443bcfa58f60bd61f2652007e12f5865797eea65f44d79b1b.jpeg",
  azureeng: "https://static.prod-images.emergentagent.com/jobs/a4d9da39-dc3c-43e0-8ce6-4ea87ce4f9ce/images/4643c78c2946e3607d1640a6044725fec3ad9b951bbe6e818ec5e784925dd66b.jpeg",
  aieng: "https://static.prod-images.emergentagent.com/jobs/a4d9da39-dc3c-43e0-8ce6-4ea87ce4f9ce/images/ec046fa26710986966f8087ce787c8992d1b383c07afb8f404b3de6b43d47880.jpeg",
  devops: "https://static.prod-images.emergentagent.com/jobs/a4d9da39-dc3c-43e0-8ce6-4ea87ce4f9ce/images/38ef528c3c0c9e855dcbb79b750516faaaca56a3740549ea658295da15f79f20.jpeg",
  servicenow: "https://static.prod-images.emergentagent.com/jobs/a4d9da39-dc3c-43e0-8ce6-4ea87ce4f9ce/images/502f8b2c834eff914df945cdec5f581d9f56f898d7ade6dcf4665e08c7fcc4f8.jpeg",
  aiconsult: "https://static.prod-images.emergentagent.com/jobs/a4d9da39-dc3c-43e0-8ce6-4ea87ce4f9ce/images/ec046fa26710986966f8087ce787c8992d1b383c07afb8f404b3de6b43d47880.jpeg",
};

const LABS = [
  {
    id: "grc",
    name: "CS GRC Analyst",
    icon: ShieldCheck,
    tagline: "Governance, risk and compliance analyst",
    description:
      "Do the actual job at a fictional fintech. Run risk assessments, keep a live risk register up to date, test controls and work through the frameworks you'll be asked about. When you're ready, talk through your decisions in a set of practice interviews and pick up a certificate.",
    route: "/interview-prep/grc",
    accent: "#0ea5a3",
    highlights: [
      { icon: Target, label: "Risk assessment project" },
      { icon: FileText, label: "Live risk register" },
      { icon: MessagesSquare, label: "Practice interviews" },
      { icon: Award, label: "Certificate at the end" },
    ],
    ready: true,
  },
  {
    id: "soc",
    name: "SOC Analyst",
    icon: Radar,
    tagline: "Security operations centre analyst",
    description:
      "Work a realistic account-compromise case, following the evidence as it comes in, and hunt through the logs with a hands-on KQL lab. Then walk through your triage, scoping and containment calls in practice interviews, from the hiring manager round to Tier 2.",
    route: "/interview-prep/soc",
    accent: "#38bdf8",
    highlights: [
      { icon: Search, label: "Incident investigation" },
      { icon: TerminalSquare, label: "KQL hunting lab" },
      { icon: MessagesSquare, label: "Tier 1 & 2 interviews" },
      { icon: Award, label: "Certificate at the end" },
    ],
    ready: true,
  },
  {
    id: "iam",
    name: "IAM Analyst",
    icon: KeyRound,
    tagline: "Identity and access management analyst",
    description:
      "Run a full access review at a fictional fintech. Dig into privileged roles, separation-of-duties conflicts, dormant accounts and risky OAuth grants, then design a clean least-privilege model. Finish by defending your calls in practice interviews.",
    route: "/interview-prep/iam",
    accent: "#a855f7",
    highlights: [
      { icon: Search, label: "Access review" },
      { icon: FileText, label: "Least-privilege design" },
      { icon: MessagesSquare, label: "Practice interviews" },
      { icon: Award, label: "Certificate at the end" },
    ],
    ready: true,
  },
  {
    id: "cloud",
    name: "Cloud Security",
    icon: Cloud,
    tagline: "Cloud security analyst",
    description:
      "Investigate a leaked AWS access key using CloudTrail and GuardDuty, then work out how you'd catch and respond to it next time. Talk through your triage, scoping and containment in practice interviews, with the shared-responsibility model along the way.",
    route: "/interview-prep/cloud",
    accent: "#f59e0b",
    highlights: [
      { icon: Search, label: "Cloud investigation" },
      { icon: TerminalSquare, label: "Detection design lab" },
      { icon: MessagesSquare, label: "Practice interviews" },
      { icon: Award, label: "Certificate at the end" },
    ],
    ready: true,
  },
  {
    id: "sysadmin",
    name: "System Administrator",
    icon: Server,
    tagline: "System administrator",
    description:
      "Track down a server outage one step at a time: a disk filling up, a stopped service, and the backup and recovery calls that follow. Write a clear troubleshooting runbook, then talk through your judgment in practice interviews.",
    route: "/interview-prep/sysadmin",
    accent: "#22c55e",
    highlights: [
      { icon: Search, label: "Server incident" },
      { icon: FileText, label: "Troubleshooting runbook" },
      { icon: MessagesSquare, label: "Practice interviews" },
      { icon: Award, label: "Certificate at the end" },
    ],
    ready: true,
  },
  {
    id: "itsupport",
    name: "IT Support",
    icon: Headset,
    tagline: "IT support and helpdesk",
    description:
      "Pick up a live P1 ticket where a VIP is locked out, and work it using Active Directory and device evidence. Write clean ticket notes, then practise both the troubleshooting and the way you talk to users in a set of interviews.",
    route: "/interview-prep/itsupport",
    accent: "#06b6d4",
    highlights: [
      { icon: Search, label: "Ticket triage" },
      { icon: FileText, label: "Ticket documentation" },
      { icon: MessagesSquare, label: "Practice interviews" },
      { icon: Award, label: "Certificate at the end" },
    ],
    ready: true,
  },
  {
    id: "cloudapp",
    name: "Cloud Application Support",
    icon: AppWindow,
    tagline: "Cloud application support",
    description:
      "Jump on a production incident where the app starts throwing errors and slowing down after a bad deploy. Use the app metrics, logs and database clues to find what broke, write up the root cause, then defend your fix in practice interviews.",
    route: "/interview-prep/cloudapp",
    accent: "#6366f1",
    highlights: [
      { icon: Search, label: "Production incident" },
      { icon: FileText, label: "Root-cause write-up" },
      { icon: MessagesSquare, label: "Practice interviews" },
      { icon: Award, label: "Certificate at the end" },
    ],
    ready: true,
  },
  {
    id: "dba",
    name: "SQL Database Administrator",
    icon: Database,
    tagline: "SQL database administrator",
    description:
      "Work a live database slowdown, where one query is scanning millions of rows and blocking everything behind it. Follow the evidence, put together a tuning and reliability plan, then talk through your indexing, locking and backup calls in practice interviews.",
    route: "/interview-prep/dba",
    accent: "#e11d48",
    highlights: [
      { icon: Search, label: "Hands-on database incident" },
      { icon: FileText, label: "Tuning & reliability lab" },
      { icon: MessagesSquare, label: "Practice interviews" },
      { icon: Award, label: "Certificate at the end" },
    ],
    ready: true,
  },
  {
    id: "m365",
    name: "Microsoft 365 Administrator",
    icon: LayoutGrid,
    tagline: "Microsoft 365 administrator",
    description:
      "Work a real tenant compromise (a finance account taken over via legacy auth, phishing sent, invoices auto-forwarded) using Entra sign-in logs, mailbox rules and message trace. Contain it, harden identity and mail flow, then defend your calls in practice interviews.",
    route: "/interview-prep/m365",
    accent: "#ea580c",
    highlights: [
      { icon: Search, label: "Account-compromise incident" },
      { icon: FileText, label: "Tenant-hardening lab" },
      { icon: MessagesSquare, label: "Practice interviews" },
      { icon: Award, label: "Certificate at the end" },
    ],
    ready: true,
  },
  {
    id: "azureeng",
    name: "Azure Cloud Engineer",
    icon: CloudCog,
    tagline: "Azure cloud engineer",
    description:
      "Chase down a production outage where an infra deploy quietly broke the app's path to its database (an NSG change), using App Service logs, the Activity Log and Azure Monitor. Fix it safely, design the guardrails, and defend your architecture in practice interviews.",
    route: "/interview-prep/azureeng",
    accent: "#0284c7",
    highlights: [
      { icon: Search, label: "Production Azure incident" },
      { icon: FileText, label: "Architecture & guardrails lab" },
      { icon: MessagesSquare, label: "Practice interviews" },
      { icon: Award, label: "Certificate at the end" },
    ],
    ready: true,
  },
  {
    id: "aieng",
    name: "AI Engineer",
    icon: BrainCircuit,
    tagline: "AI engineer",
    description:
      "Debug a production RAG assistant that suddenly hallucinates and slows down after a release (an embedding swap with no re-index, plus a dropped grounding prompt). Trace it through eval, retrieval and latency data, design an evaluated, grounded system, and defend it in practice interviews.",
    route: "/interview-prep/aieng",
    accent: "#7c3aed",
    highlights: [
      { icon: Search, label: "RAG production incident" },
      { icon: FileText, label: "RAG design & evals lab" },
      { icon: MessagesSquare, label: "Practice interviews" },
      { icon: Award, label: "Certificate at the end" },
    ],
    ready: true,
  },
  {
    id: "devops",
    name: "DevOps / Kubernetes Engineer",
    icon: Container,
    tagline: "DevOps / Kubernetes engineer",
    description:
      "Recover a bad Kubernetes release where pods are in CrashLoopBackOff and the service is throwing 503s (a renamed Secret key the Deployment still points at). Debug it with kubectl, roll back safely, design a safe delivery pipeline, and defend your calls in practice interviews.",
    route: "/interview-prep/devops",
    accent: "#16a34a",
    highlights: [
      { icon: Search, label: "Kubernetes rollout incident" },
      { icon: FileText, label: "Safe-delivery pipeline lab" },
      { icon: MessagesSquare, label: "Practice interviews" },
      { icon: Award, label: "Certificate at the end" },
    ],
    ready: true,
  },
  {
    id: "servicenow",
    name: "ServiceNow Administrator",
    icon: Workflow,
    tagline: "ServiceNow system administrator",
    description:
      "Recover a real platform outage where, right after an Update Set hit production, agents can't save incidents and see 'Security constraints' errors (an ACL that now demands the wrong role). Trace it through ACLs, roles and logs, back it out safely, design a safe change process, and defend your calls in practice interviews.",
    route: "/interview-prep/servicenow",
    accent: "#12a150",
    highlights: [
      { icon: Search, label: "ACL / Update Set incident" },
      { icon: FileText, label: "Safe-change design lab" },
      { icon: MessagesSquare, label: "Practice interviews" },
      { icon: Award, label: "Certificate at the end" },
    ],
    ready: true,
  },
  {
    id: "aiconsult",
    name: "AI Consultant",
    icon: Lightbulb,
    tagline: "AI strategy & solutions consultant",
    description:
      "Rescue a stalled GenAI 'Claims Copilot' pilot the client wants to cancel (outdated policy documents, no evaluation set, lost access controls, zero change management). Diagnose it from the evidence, frame options for the steering committee, design a discovery-to-governance roadmap, and defend your recommendations in practice interviews.",
    route: "/interview-prep/aiconsult",
    accent: "#d97706",
    highlights: [
      { icon: Search, label: "Stalled AI pilot case" },
      { icon: FileText, label: "AI roadmap & governance lab" },
      { icon: MessagesSquare, label: "Practice interviews" },
      { icon: Award, label: "Certificate at the end" },
    ],
    ready: true,
  },
];

export default function InterviewPrepHub() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    api.get("/interview-prep/summary").then(({ data }) => setSummary(data.labs || [])).catch(() => setSummary([]));
  }, []);
  const attempted = (summary || []).filter((l) => l.best_overall > 0);
  const readyCount = attempted.filter((l) => l.ready).length;
  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="interview-prep-hub">
      <div className="flex items-center gap-3 mb-2">
        <span className="h-11 w-11 rounded-xl grid place-items-center bg-[#0ea5a3]/15 text-[#0ea5a3]"><ShieldCheck className="h-6 w-6" /></span>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Interview Prep Lab</h1>
          <p className="text-sm text-muted-foreground">Practise the real work and the interview questions that come with it, so you walk in ready.</p>
        </div>
      </div>

      {attempted.length > 0 && (
        <div className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="readiness-tracker">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-[#0ea5a3]" />
            <h2 className="text-sm font-semibold">Your readiness</h2>
            <span className="ml-auto text-xs text-muted-foreground">{readyCount} of {attempted.length} interview-ready</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {attempted.map((l) => {
              const modes = Object.keys(l.mode_labels || {});
              return (
                <div key={l.prefix} className="rounded-xl border border-border p-3" data-testid={`readiness-${l.prefix}`} style={{ ["--acc"]: l.accent }}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold truncate">{l.name}</span>
                    {l.ready
                      ? <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2 py-0.5 text-white" style={{ background: l.accent }} data-testid={`ready-badge-${l.prefix}`}><CheckCircle2 className="h-3 w-3" /> READY</span>
                      : <span className="ml-auto text-[10px] font-medium rounded-full px-2 py-0.5 border border-border text-muted-foreground">In progress</span>}
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${l.best_overall}%`, background: l.accent }} />
                  </div>
                  <div className="mt-1.5 flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>Best {l.best_overall}/100</span>
                    <span>{(l.cleared || []).length}/{modes.length} modes cleared</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {modes.map((m) => {
                      const cleared = (l.cleared || []).includes(m);
                      const score = (l.best || {})[m] || 0;
                      return (
                        <span key={m} className={`text-[10px] rounded-full px-2 py-0.5 border ${cleared ? "text-white border-transparent" : "border-border text-muted-foreground"}`}
                          style={cleared ? { background: l.accent } : {}} title={`${score}/100`}>
                          {l.mode_labels[m]}{score > 0 ? ` · ${score}` : ""}
                        </span>
                      );
                    })}
                  </div>
                  <button onClick={() => navigate(`/interview-prep/${l.prefix}`)} className="mt-2 text-[11px] font-medium text-[color:var(--acc)]" data-testid={`readiness-open-${l.prefix}`}>
                    Continue →
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mt-8">
        {LABS.map((lab) => (
          <button
            key={lab.id}
            onClick={() => navigate(lab.route)}
            data-testid={`open-lab-${lab.id}`}
            className="group flex flex-col items-center justify-center text-center rounded-2xl border border-border bg-card px-4 py-8 hover:-translate-y-1 hover:shadow-xl hover:border-[color:var(--acc)] transition-all duration-200"
            style={{ ["--acc"]: lab.accent }}
          >
            <span
              className="h-24 w-24 rounded-2xl overflow-hidden grid place-items-center shadow-lg transition-transform duration-200 group-hover:scale-110"
              style={{ boxShadow: `0 12px 34px -10px ${lab.accent}90` }}
            >
              <img src={LOGOS[lab.id]} alt={`${lab.name} logo`} className="h-full w-full object-cover scale-[1.42]" loading="lazy" />
            </span>
            <div className="mt-4 font-semibold text-base leading-tight">{lab.name}</div>
          </button>
        ))}
        <div className="flex flex-col items-center justify-center text-center rounded-2xl border border-dashed border-border px-4 py-8 text-sm text-muted-foreground">
          <span className="h-20 w-20 rounded-2xl grid place-items-center bg-muted"><ArrowRight className="h-8 w-8 opacity-40" /></span>
          <div className="mt-4 font-medium">More tracks on the way</div>
        </div>
      </div>
    </div>
  );
}
